// Default schedule model + helpers for Fresh Meet events.
// Uses CSS classes defined in SchedulePage.css for consistent styling.

export const SCHEDULE_COLOR_ORANGE = '#F47B20';
export const SCHEDULE_COLOR_BLACK = '#1A1A1A';
export const SCHEDULE_COLOR_CHARCOAL = '#3D3D3D';
export const SCHEDULE_COLOR_GRAY = '#C8C8C8';
export const SCHEDULE_COLOR_SUN = '#D96510';

const STABLE_SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/** Published schedule is locked in; visitors are not warned about changes. */
export const SCHEDULE_CERTAINTY_CONFIRMED = 'confirmed';
/** Early / draft schedule; visitors see a prominent tentative notice. */
export const SCHEDULE_CERTAINTY_PRELIMINARY = 'preliminary';

export function defaultAddOnCallout() {
  return {
    title: 'Add-Ons',
    body: '',
    linkUrl: '',
    linkLabel: '',
  };
}

export function defaultScheduleCta() {
  return {
    headline: 'Secure Your Spot',
    buttonLabel: 'Register Now',
    closedButtonLabel: 'Get on the interest list',
    interestListUrl: '',
  };
}

export const DEFAULT_SCHEDULE_FIELDS = {
  title: 'FRESH MEET 2026',
  /** Shown as main H1 (draft: "Schedule"). */
  heroTitle: 'Schedule',
  /** Optional line under H1 (e.g. tagline). */
  subtitle: 'Schedule of Events',
  /** `preliminary` shows a clear tentative banner on the public schedule. */
  scheduleCertainty: SCHEDULE_CERTAINTY_CONFIRMED,
  introBody:
    'Five days at Skydive Perris. The full schedule is below — Wednesday through Friday are training days with optional add-ons, Saturday is competition day, Sunday is the Funf\'Air free-for-all.',
  addOnCallout: {
    title: 'Add-Ons',
    body:
      'Tunnel camp, canopy courses, water training, jet jumps, and more can be added when you register.',
    linkUrl: '',
    linkLabel: 'Register',
  },
  cta: {
    headline: 'Secure Your Spot',
    buttonLabel: 'Register Now',
    closedButtonLabel: 'Get on the interest list',
    interestListUrl: '',
  },
  footerNote: 'Times subject to change based on weather and aircraft operations.',
  allWeekSlug: 'all-week-long',
  allWeekLines: [
    'Free load organizing from Perris pros',
    'Gear manufacturer tents all weekend',
    'Sky-Curious speed event — meet the experts',
    'Photo scavenger hunt across all four days',
    'Event goody bag for every registrant',
    'Event t-shirt (register early to guarantee yours)',
    'DC-9 Jet Jumps available',
  ],
  days: [
    {
      id: 'wednesday',
      stableSlug: 'wednesday',
      dayLabel: 'Arrival Day',
      accentColor: SCHEDULE_COLOR_CHARCOAL,
      cardEmoji: '🛬',
      cardSubtitle: 'Arrive & train',
      cardBodyLines: [
        'Get settled, make practice jumps, and take advantage of free load organizing from Perris staff.',
        'Get your recurrency jump if you need it. Add tunnel time, a canopy course, or water training.',
      ],
      header: 'WEDNESDAY – Welcome Day',
      headerClass: 'schedule-day-header-wed',
      main: false,
      rows: [
        {
          id: 'wed-1',
          type: 'normal',
          time: '7:00 am',
          label: 'Manifest Opens / Gold State Gear Opens',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'wed-2',
          type: 'normal',
          time: '7:30 am',
          label: 'Bombshelter Restaurant Opens',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'wed-3',
          type: 'normal',
          time: '8:00 am',
          label: 'Meet with Dave for your recurrency',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'wed-4',
          type: 'normal',
          time: '8:00 am – All Day',
          label: 'Jumping / Load Organizing, Freshman Orientation',
          highlight: true,
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'wed-5',
          type: 'normal',
          time: '6:00 pm',
          label: 'Meet Christy, have a drink at the Bombshelter!',
          timeKind: 'exact',
          note: '',
        },
      ],
    },
    {
      id: 'thursday',
      stableSlug: 'thursday',
      dayLabel: 'Training Day',
      accentColor: SCHEDULE_COLOR_CHARCOAL,
      cardEmoji: '📅',
      cardSubtitle: 'Train & fly',
      cardBodyLines: [],
      header: 'THURSDAY – Train & Fly',
      headerClass: 'schedule-day-header-thu',
      main: false,
      rows: [
        { id: 'thu-1', type: 'normal', time: '7:00 am', label: 'Manifest Opens', timeKind: 'exact', note: '' },
        {
          id: 'thu-2',
          type: 'normal',
          time: '7:30 am',
          label: 'Bombshelter Restaurant Opens / Gold State Gear Opens',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'thu-3',
          type: 'normal',
          time: '8:00 am – All Day',
          label: 'Jumping / Load Organizing, Freshman Orientation',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'thu-4',
          type: 'normal',
          time: '8:00 am',
          label: 'Water Training Class / Canopy Control Course',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'thu-5',
          type: 'normal',
          time: '2:00 – 8:00 pm',
          label: 'Perris Indoor Tunnel Sessions',
          timeKind: 'exact',
          note: '',
        },
      ],
    },
    {
      id: 'friday',
      stableSlug: 'friday',
      dayLabel: 'Training Day',
      accentColor: SCHEDULE_COLOR_CHARCOAL,
      cardEmoji: '🎪',
      cardSubtitle: 'Skill + social',
      cardBodyLines: [],
      header: 'FRIDAY – Skill + Social',
      headerClass: 'schedule-day-header-fri',
      main: false,
      rows: [
        { id: 'fri-1', type: 'normal', time: '7:00 am', label: 'Manifest Opens', timeKind: 'exact', note: '' },
        {
          id: 'fri-2',
          type: 'normal',
          time: '7:30 am',
          label: 'Bombshelter Restaurant Opens / Gold State Gear Opens',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'fri-3',
          type: 'normal',
          time: '8:00 am – All Day',
          label: 'Jumping / Load Organizing, Freshman Orientation',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'fri-4',
          type: 'normal',
          time: '8:00 am',
          label: 'Water Training Class / Canopy Control Course',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'fri-callout',
          type: 'callout',
          variant: 'fri',
          time: 'Evening',
          timeKind: 'generic',
          label: 'Sky Curious Event (poolside)',
          stableSlug: 'friday-sky-curious',
          note: 'Dinner through Bombshelter • Bring a jacket!',
        },
      ],
    },
    {
      id: 'saturday',
      stableSlug: 'saturday',
      dayLabel: 'Main Event',
      accentColor: SCHEDULE_COLOR_ORANGE,
      cardEmoji: '🏆',
      cardSubtitle: 'The main event',
      cardBodyLines: [
        'Your first 3-way competition. Two freshlings plus an experienced mentor, four skydives, and a full DZ scavenger hunt.',
        'Points aren’t just in the air — the ground game matters. Raffle Saturday evening.',
      ],
      header: 'SATURDAY – MAIN EVENT DAY',
      headerClass: 'schedule-day-header-sat',
      main: true,
      rows: [
        { id: 'sat-1', type: 'normal', time: '6:30 am', label: 'Manifest Opens', timeKind: 'exact', note: '' },
        {
          id: 'sat-2',
          type: 'normal',
          time: '7:00 am',
          label: 'Bombshelter Restaurant Opens',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sat-3',
          type: 'normal',
          time: '7:00 – 7:59 am',
          label: 'Registration',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sat-4',
          type: 'normal',
          time: '7:30 am',
          label: 'Gold State Gear Opens',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sat-main',
          type: 'main-block',
          stableSlug: 'saturday-main',
          items: [
            {
              id: 'sat-main-1',
              time: 'Morning',
              timeKind: 'generic',
              label: 'Meet Briefing and Kick-off',
              stableSlug: 'saturday-briefing',
              note: '',
            },
            {
              id: 'sat-main-2',
              time: 'Day of',
              timeKind: 'generic',
              label: 'MAIN EVENT! The Fresh Meet competition',
              stableSlug: 'saturday-competition',
              note: '',
            },
          ],
          note: 'You’ll be busy all day — don’t book anything else!',
        },
        {
          id: 'sat-5',
          type: 'normal',
          time: '10:00 am',
          label: 'All Puzzles open at HQ',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sat-6',
          type: 'normal',
          time: '10:00 am – 4:30 pm',
          label: 'Bucket Raffle at HQ',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sat-7',
          type: 'normal',
          time: '3:30 – 4:30 pm',
          label: 'T-shirt Exchange',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sat-8',
          type: 'normal',
          time: '3:45 pm',
          label: 'Mic Drop Voting Starts',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sat-9',
          type: 'normal',
          time: '4:30 pm',
          label: 'Bonus clues stop',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sat-callout',
          type: 'callout',
          variant: 'sat',
          stableSlug: 'saturday-evening',
          items: [
            {
              id: 'sat-callout-1',
              time: '5:00 pm',
              label: 'Group Photo (Wear your Fresh Meet T-shirt!) and Awards',
              timeKind: 'exact',
              note: '',
            },
            {
              id: 'sat-callout-2',
              time: '6:00 pm',
              label: 'Raffle Party in the Bombshelter (must be present to win!)',
              timeKind: 'exact',
              note: '',
            },
          ],
        },
      ],
    },
    {
      id: 'sunday',
      stableSlug: 'sunday',
      dayLabel: "Fresh Funf'Air",
      accentColor: SCHEDULE_COLOR_SUN,
      cardEmoji: '🎪',
      cardSubtitle: 'Fresh Funf’Air',
      cardBodyLines: [
        'Go at your own pace. Skyvan jumps, high-altitude dives, games, and adventures — pick yours and earn points all day.',
      ],
      header: 'SUNDAY – Bonus & Big Jumps',
      headerClass: 'schedule-day-header-sun',
      main: false,
      rows: [
        {
          id: 'sun-1',
          type: 'normal',
          time: '7:00 am / 7:30 am',
          label: 'Bombshelter Restaurant Opens / Gold State Gear Opens',
          timeKind: 'exact',
          note: '',
        },
        {
          id: 'sun-2',
          type: 'normal',
          time: '8:00 am – 4:00 pm',
          label: 'Pick up your Patch card at HQ for the Fresh Meet FunfAir',
          timeKind: 'exact',
          note: '',
        },
        { id: 'sun-3', type: 'normal', time: '8:00 am', label: 'Extra T-shirts for sale', timeKind: 'exact', note: '' },
        {
          id: 'sun-4',
          type: 'normal',
          time: '8:00 am – All Day',
          label: 'Jumping / Load Organizing',
          timeKind: 'exact',
          note: '',
        },
        { id: 'sun-5', type: 'normal', time: '8:00 am', label: 'Jet Brief', timeKind: 'exact', note: '' },
        {
          id: 'sun-6',
          type: 'normal',
          time: '10:30 am',
          label: 'O2 Briefing / High Altitude Jumps',
          timeKind: 'exact',
          note: '',
        },
        { id: 'sun-7', type: 'normal', time: '4:00 pm', label: 'Patch deadline', timeKind: 'exact', note: '' },
      ],
    },
  ],
};

export function migrateScheduleFieldsForEditor(raw) {
  const base =
    typeof raw === 'object' && raw
      ? JSON.parse(JSON.stringify(raw))
      : JSON.parse(JSON.stringify(DEFAULT_SCHEDULE_FIELDS));
  if (typeof base.introBody !== 'string') base.introBody = '';
  if (typeof base.heroTitle !== 'string' || !base.heroTitle.trim()) {
    base.heroTitle = 'Schedule';
  }
  if (!base.addOnCallout || typeof base.addOnCallout !== 'object') {
    base.addOnCallout = defaultAddOnCallout();
  } else {
    const a = defaultAddOnCallout();
    base.addOnCallout = { ...a, ...base.addOnCallout };
  }
  if (!base.cta || typeof base.cta !== 'object') {
    base.cta = defaultScheduleCta();
  } else {
    const c = defaultScheduleCta();
    base.cta = { ...c, ...base.cta };
  }
  if (!Array.isArray(base.allWeekLines)) base.allWeekLines = [];
  if (typeof base.allWeekSlug !== 'string') base.allWeekSlug = '';
  base.scheduleCertainty =
    base.scheduleCertainty === SCHEDULE_CERTAINTY_PRELIMINARY
      ? SCHEDULE_CERTAINTY_PRELIMINARY
      : SCHEDULE_CERTAINTY_CONFIRMED;
  base.days = (base.days || []).map((day) => {
    const cardBodyLines = Array.isArray(day.cardBodyLines) ? day.cardBodyLines : [];
    const accentDefault = day.main ? SCHEDULE_COLOR_ORANGE : SCHEDULE_COLOR_CHARCOAL;
    const dayLabel =
      typeof day.dayLabel === 'string' && day.dayLabel.trim()
        ? day.dayLabel
        : typeof day.cardSubtitle === 'string' && day.cardSubtitle.trim()
          ? day.cardSubtitle.trim()
          : '';
    const accentColor =
      typeof day.accentColor === 'string' && day.accentColor.trim()
        ? day.accentColor.trim()
        : accentDefault;
    return {
      ...day,
      dayLabel,
      accentColor,
      stableSlug: typeof day.stableSlug === 'string' ? day.stableSlug : day.id || '',
      cardSubtitle: typeof day.cardSubtitle === 'string' ? day.cardSubtitle : '',
      cardEmoji: typeof day.cardEmoji === 'string' ? day.cardEmoji : '',
      cardBodyLines,
      rows: (day.rows || []).map((row) => {
        const r = {
          ...row,
          timeKind: row.timeKind === 'generic' ? 'generic' : 'exact',
          note: typeof row.note === 'string' ? row.note : '',
        };
        if (Array.isArray(row.items)) {
          r.items = row.items.map((item) => ({
            ...item,
            timeKind: item.timeKind === 'generic' ? 'generic' : 'exact',
            stableSlug: typeof item.stableSlug === 'string' ? item.stableSlug : '',
            note: typeof item.note === 'string' ? item.note : '',
          }));
        } else if (row.type === 'main-block' || (row.type === 'callout' && row.variant === 'sat')) {
          r.items = [];
        }
        r.stableSlug = typeof row.stableSlug === 'string' ? row.stableSlug : '';
        return r;
      }),
    };
  });
  return base;
}

export function validateScheduleStableSlugs(fields) {
  const seen = new Set();
  const errors = [];
  const touch = (raw, label) => {
    const s = (raw || '').toString().trim().toLowerCase();
    if (!s) return;
    if (!STABLE_SLUG_RE.test(s)) {
      errors.push(
        `${label}: invalid anchor slug "${raw}" (use lowercase letters, numbers, hyphens only).`
      );
      return;
    }
    if (seen.has(s)) errors.push(`Duplicate anchor slug: ${s}.`);
    seen.add(s);
  };
  touch(fields.allWeekSlug, 'All-week card');
  (fields.days || []).forEach((day) => {
    touch(day.stableSlug, `Day "${(day.header || day.id || '').toString().slice(0, 40)}"`);
    (day.rows || []).forEach((row) => {
      touch(row.stableSlug, `Row under "${(day.header || day.id || '').toString().slice(0, 32)}"`);
      (row.items || []).forEach((item) => {
        touch(
          item.stableSlug,
          `Item under "${(day.header || day.id || '').toString().slice(0, 32)}"`
        );
      });
    });
  });
  return errors;
}

export function normalizeScheduleSlugFields(fields) {
  const lower = (v) => (v || '').toString().trim().toLowerCase();
  const clone = JSON.parse(JSON.stringify(fields));
  clone.allWeekSlug = lower(clone.allWeekSlug);
  clone.days = (clone.days || []).map((day) => ({
    ...day,
    stableSlug: lower(day.stableSlug),
    rows: (day.rows || []).map((row) => {
      const { items, ...rowSansItems } = row;
      const next = {
        ...rowSansItems,
        stableSlug: lower(row.stableSlug),
      };
      if (Array.isArray(items)) {
        next.items = items.map((item) => ({
          ...item,
          stableSlug: lower(item.stableSlug),
        }));
      } else if (row.type === 'main-block' || (row.type === 'callout' && row.variant === 'sat')) {
        next.items = [];
      }
      return next;
    }),
  }));
  clone.scheduleCertainty =
    clone.scheduleCertainty === SCHEDULE_CERTAINTY_PRELIMINARY
      ? SCHEDULE_CERTAINTY_PRELIMINARY
      : SCHEDULE_CERTAINTY_CONFIRMED;
  return clone;
}

function parseScheduleTimeToMinutes(raw) {
  if (!raw) return Number.POSITIVE_INFINITY;
  const text = String(raw).toLowerCase();
  const matches = [...text.matchAll(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/g)];
  if (!matches.length) return Number.POSITIVE_INFINITY;
  const first = matches[0];
  const lastWithPeriod = [...matches].reverse().find((m) => m[3]);
  const period = (first[3] || (lastWithPeriod && lastWithPeriod[3]) || '').toLowerCase();
  let hour = parseInt(first[1], 10);
  const minute = first[2] != null ? parseInt(first[2], 10) : 0;
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return Number.POSITIVE_INFINITY;
  if (period === 'am') {
    if (hour === 12) hour = 0;
  } else if (period === 'pm') {
    if (hour !== 12) hour += 12;
  }
  return hour * 60 + minute;
}

/** Sort rows within each day by parsed clock time (ties keep original order). */
export function sortScheduleFieldsByTime(fields) {
  const sortedDays = (fields.days || []).map((day) => {
    const rowsWithIndex = (day.rows || []).map((row, index) => ({
      row,
      index,
      minutes: parseScheduleTimeToMinutes(row.time),
    }));
    rowsWithIndex.sort((a, b) => {
      if (a.minutes === b.minutes) {
        return a.index - b.index;
      }
      return a.minutes - b.minutes;
    });
    return {
      ...day,
      rows: rowsWithIndex.map((entry) => entry.row),
    };
  });
  return { ...fields, days: sortedDays };
}

function esc(str) {
  return String(str == null ? '' : str);
}

function rowNoteHtml(note) {
  const n = (note || '').toString().trim();
  return n ? `<div class="schedule-callout-note">${esc(n)}</div>` : '';
}

export function buildScheduleHtmlFromFields(fields) {
  const isPreliminary = fields.scheduleCertainty === SCHEDULE_CERTAINTY_PRELIMINARY;
  const preliminaryBlock = isPreliminary
    ? `<div class="schedule-sheet-preliminary-banner" role="status">` +
      `<span class="schedule-sheet-preliminary-banner__label">Preliminary schedule</span>` +
      `<span class="schedule-sheet-preliminary-banner__text">Times and activities are tentative and may change. Check back for updates.</span>` +
      `</div>`
    : '';
  const introBlock = fields.introBody
    ? `<p class="schedule-intro-body">${esc(fields.introBody)}</p>`
    : '';
  const add = fields.addOnCallout || defaultAddOnCallout();
  const addBody = (add.body || '').trim();
  const addOnBlock =
    add.title || addBody || add.linkUrl
      ? `<div class="schedule-addon-callout">` +
        `<span class="schedule-addon-title">${esc(add.title || 'Add-Ons')}</span>` +
        (addBody ? `<span class="schedule-addon-body"> — ${esc(addBody)}</span>` : '') +
        (add.linkUrl
          ? ` <a class="schedule-addon-link" href="${esc(add.linkUrl)}">${esc(add.linkLabel || 'Link')}</a>`
          : '') +
        `</div>`
      : '';

  const daysHtml = fields.days
    .map((day) => {
      const dayCards = [];
      dayCards.push(
        `<div class="schedule-day-card${day.main ? ' schedule-day-main' : ''}">` +
          `<div class="schedule-day-header ${day.headerClass || ''}">` +
          `${esc(day.header)}` +
          `</div>` +
          `<div class="schedule-day-body">`
      );

      day.rows.forEach((row) => {
        if (row.type === 'normal') {
          dayCards.push(
            `<div class="schedule-time-row"><span class="schedule-time-strong">${esc(
              row.time
            )}</span> – ${esc(row.label)}</div>` +
              rowNoteHtml(row.note)
          );
        } else if (row.type === 'callout' && row.variant === 'fri') {
          dayCards.push(
            `<div class="schedule-callout schedule-callout-fri">` +
              `<div><span class="schedule-time-strong">${esc(row.time)}</span> – ${esc(
                row.label
              )}</div>` +
              rowNoteHtml(row.note) +
              `</div>`
          );
        } else if (row.type === 'main-block') {
          dayCards.push(
            `<div class="schedule-main-block">` +
              row.items
                .map(
                  (item) =>
                    `<div><span class="schedule-time-strong">${esc(item.time)}</span> – ${esc(
                      item.label
                    )}</div>` + rowNoteHtml(item.note)
                )
                .join('') +
              rowNoteHtml(row.note) +
              `</div>`
          );
        } else if (row.type === 'callout' && row.variant === 'sat') {
          dayCards.push(
            `<div class="schedule-callout schedule-callout-sat">` +
              row.items
                .map(
                  (item) =>
                    `<div><span class="schedule-time-strong">${esc(item.time)}</span> – ${esc(
                      item.label
                    )}</div>` + rowNoteHtml(item.note)
                )
                .join('') +
              `</div>`
          );
        }
      });

      dayCards.push(`</div></div>`);
      return dayCards.join('');
    })
    .join('\n\n');

  const heroTitle = (fields.heroTitle || 'Schedule').trim();
  const subdeck =
    (fields.subtitle || '').trim() && (fields.subtitle || '').trim() !== heroTitle
      ? `<div class="schedule-subtitle">${esc(fields.subtitle)}</div>`
      : '';

  return `
<div class="schedule-root">
  ${preliminaryBlock}
  <div class="schedule-header">
    <div class="schedule-title">${esc(fields.title)}</div>
    <div class="schedule-hero-heading">${esc(heroTitle)}</div>
    ${subdeck}
  </div>
  ${introBlock}
  ${addOnBlock}

  ${daysHtml}

  <div class="schedule-footer-note">
    ${esc(fields.footerNote)}
  </div>
</div>
`.trim();
}

export const DEFAULT_SCHEDULE_HTML = buildScheduleHtmlFromFields(DEFAULT_SCHEDULE_FIELDS);
